import { useState } from 'react';
import { reportApi } from '../../../../api/reportApi';

const DESTINATION_LABELS = { faicon: 'FAICON', wordpress: 'WordPress' };

/** Where the session report can go: wherever the session or its event is linked. */
const publishDestinations = (session) => [
  (session?.faicon_session_id || session?.event_faicon_id) && 'faicon',
  (session?.wp_session_id || session?.event_wp_id) && 'wordpress',
].filter(Boolean);

/** Publishing the official report, one request per selected language. */
export function usePublishReport(id, session, languages, primaryLanguage, notify, reloadSession) {
  const [destination, setDestination] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);

  const langLabel = (code) => languages.find((l) => l.code === code)?.name || code.toUpperCase();

  const confirm = async (selectedLangs = [primaryLanguage], apiVersion = 'production') => {
    const langs = (Array.isArray(selectedLangs) ? selectedLangs : [selectedLangs]).filter(Boolean);
    if (!destination || langs.length === 0) return;

    setIsPublishing(true);
    const published = [];
    const failed = [];
    try {
      const results = [];
      for (const lang of langs) {
        try {
          const result = destination === 'faicon'
            ? await reportApi.publishToFaicon(id, lang)
            : await reportApi.publishToWordPress(id, lang, apiVersion);
          published.push(lang);
          results.push(result);
        } catch (e) {
          failed.push({ lang, message: e.message || 'Unknown error' });
        }
      }
      if (published.length) await reloadSession();

      const where = DESTINATION_LABELS[destination]
        + (destination === 'wordpress' && apiVersion !== 'production' ? ` (${apiVersion} test API)` : '');
      const failedText = failed.map(({ lang, message }) => `${langLabel(lang)} (${message})`).join('; ');

      const chatbotLinked = results.some((result) => result?.chatbot_id && !result?.chatbot_error);
      const chatbotError = chatbotLinked
        ? null
        : results.map((result) => result?.chatbot_error).find(Boolean);
      const chatbotNote = chatbotError
        ? ` The event chatbot was not updated: ${chatbotError}`
        : chatbotLinked
          ? ' The event chatbot is linked. It answers from this session after ingest finishes.'
          : '';

      if (!failed.length) {
        notify({
          type: chatbotError ? 'warning' : 'success',
          title: chatbotError ? 'Report published, chatbot not updated' : 'Report published successfully',
          message: (langs.length > 1
            ? `Published ${published.length} language versions to ${where}: ${published.map(langLabel).join(', ')}.`
            : `Report has been published to ${where}.`) + chatbotNote,
        });
        setDestination(null);
      } else if (!published.length) {
        notify({ type: 'error', title: 'Failed to publish report', message: failedText });
      } else {
        notify({
          type: 'warning',
          title: 'Partial publish completed',
          message: `Published to ${where}: ${published.map(langLabel).join(', ')}. Failed: ${failedText}.${chatbotNote}`,
        });
        setDestination(null);
      }
    } finally {
      setIsPublishing(false);
    }
  };

  return {
    destinations: publishDestinations(session),
    destination,
    open: setDestination,
    close: () => setDestination(null),
    setDestination,
    isPublishing,
    confirm,
  };
}
